import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { getMyBooking, requestBookingCancellation, type MyBooking } from '@/api/generated';
import { PriceBreakdown } from '@/components/PriceBreakdown';
import { Button, buttonVariants } from '@/components/ui/button';
import { StatusChip } from '@/components/StatusChip';
import { formatMoney, fromGuestParty } from '@/lib/booking';
import { partyLabel } from '@/lib/party';
import { company } from '@/lib/company';
import { applySeo } from '@/lib/seo';
import { resolveExistingBookingCancellationLabel } from '@/lib/calendar';
import { m } from '@/paraglide/messages';

// Booking detail (/cabinet/bookings/$id) — OH-94.
//
// The cabinet list answers "do I have a booking"; this page answers everything
// a guest actually comes back for: what was paid, for whom, under what
// cancellation terms, and how to get out of it. Before this the cards were
// inert, and a guest wanting to cancel had to find an email address somewhere
// on the site and hope.
//
// A page rather than an expanding card, deliberately: it has a URL a guest can
// send themselves, and it is where the arrival details (OH-96) and the guest
// registration form (OH-97) will land without a second redesign.
export const Route = createFileRoute('/cabinet/bookings/$id')({
  component: BookingDetailPage,
});

function BookingDetailPage() {
  const { id } = Route.useParams();

  useEffect(() => {
    return applySeo({ title: 'Your booking — ollio', noindex: true });
  }, []);

  const bookingQuery = useQuery<MyBooking | null>({
    queryKey: ['my-booking', id],
    queryFn: async () => {
      const { data, response } = await getMyBooking({ path: { id } });
      // 401 and 404 are the same answer to the guest: this is not a booking
      // you can see. The server deliberately does not distinguish "not yours"
      // from "does not exist" (anti-enumeration), and neither does this.
      if (response?.status === 401 || response?.status === 404) return null;
      if (!data) throw new Error('booking_fetch_failed');
      return data;
    },
    retry: false,
  });

  if (bookingQuery.isPending) {
    return (
      <main className="mx-auto w-full max-w-2xl flex-1 px-6 py-12">
        <p className="text-sm text-muted">Loading your booking…</p>
      </main>
    );
  }

  if (!bookingQuery.data) {
    return (
      <main className="mx-auto w-full max-w-2xl flex-1 px-6 py-12">
        <h1 className="font-serif text-2xl font-semibold text-[var(--ollio-ink)]">
          We couldn&apos;t find that booking
        </h1>
        <p className="mt-2 text-muted">
          It may belong to a different account, or the link may be out of date.
        </p>
        <Link to="/cabinet" className={`${buttonVariants({ variant: 'primary', size: 'md' })} mt-6`}>
          Back to your bookings
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-2xl flex-1 px-6 py-12">
      <Link to="/cabinet" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Your bookings
      </Link>
      <BookingDetail booking={bookingQuery.data} />
    </main>
  );
}

// BookingDetail is exported for tests: the page wrapper is a data fetch, and
// what is worth pinning is what the guest is told about a booking.
export function BookingDetail({ booking }: { booking: MyBooking }) {
  const total = formatMoney(booking.total_amount_minor, booking.currency);
  const deadlineLabel = resolveExistingBookingCancellationLabel(
    booking.status,
    booking.free_cancellation,
  );
  const party = booking.guests ? partyLabel(fromGuestParty(booking.guests)) : null;
  const breakdown = booking.price_breakdown;

  return (
    <div className="mt-6 rounded-[var(--radius-2xl)] border border-[var(--border-hairline)] bg-card p-7 shadow-[var(--shadow-lg)]">
      <StatusChip status={booking.status} />
      <h1 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-[var(--ollio-ink)]">
        {booking.apartment_title}
      </h1>

      <dl className="mt-6 space-y-2 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-4 py-3 text-sm">
        <Row label="Check-in" value={booking.checkin_date} />
        <Row label="Check-out" value={booking.checkout_date} />
        <Row label="Nights" value={`${booking.nights} ${booking.nights === 1 ? 'night' : 'nights'}`} />
        {/* Absent is not empty. An OTA booking never recorded a party, and a
            "0 guests" row would be a claim rather than a gap. */}
        {party ? <Row label="Guests" value={party} /> : null}
        {booking.ota_reservation_code ? (
          <Row label="Reservation" value={booking.ota_reservation_code} mono />
        ) : null}
      </dl>

      <div className="mt-6">
        {breakdown ? (
          <PriceBreakdown
            lineItems={breakdown.line_items}
            nights={breakdown.nights}
            totalMinor={booking.total_amount_minor}
            currency={booking.currency}
          />
        ) : (
          <div className="flex items-baseline justify-between rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-3 py-2.5">
            <span className="text-sm text-muted">Total</span>
            <span className="font-mono text-lg font-semibold tabular-nums text-[var(--ollio-ink)]">
              {total}
            </span>
          </div>
        )}
      </div>

      {deadlineLabel ? (
        <p className="mt-4 text-sm text-muted">{m.cancellation_deadline({ date: deadlineLabel })}</p>
      ) : null}

      <div className="mt-8 border-t border-[var(--border-hairline)] pt-6">
        {booking.self_service && isCancellable(booking.status) ? (
          <CancellationSection booking={booking} />
        ) : (
          <>
            <h2 className="font-serif text-lg font-semibold text-[var(--ollio-ink)]">
              Booked through {booking.ota_name}
            </h2>
            {/* No cancel button here on purpose: this booking is the channel's
                to change, and a button we cannot honour is worse than none. */}
            <p className="mt-1.5 text-sm text-muted">
              Changes and cancellations for this stay go through {booking.ota_name}, where you
              booked it.
            </p>
          </>
        )}

        <p className="mt-4 text-sm text-muted">
          Anything else — write to{' '}
          <a className="underline" href={`mailto:${company.bookingsEmail}`}>
            {company.bookingsEmail}
          </a>{' '}
          or call{' '}
          <a className="underline" href={`tel:${company.phone.replace(/\s/g, '')}`}>
            {company.phone}
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className={`font-medium text-[var(--ollio-ink)] ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

// CancellationSection is the "get me out of this" half of the page.
//
// The request does NOT cancel anything — an operator decides the refund under
// the booking's own policy snapshot — so the wording promises a reply, not an
// outcome. Once filed, the control is replaced by a statement of fact: the
// same button offered twice is how one guest becomes two conversations in the
// operator's inbox.
// isCancellable reports whether there is still a stay to call off. A booking
// that is already cancelled, was a no-show, or has been checked out has nothing
// left to cancel — and offering the control anyway (OH-160) reads as "we did
// not notice", which is worse than offering nothing.
//
// Deliberately a positive list: a status added later must be opted IN to the
// control rather than inheriting it by falling through a `!== 'cancelled'`.
function isCancellable(status: string): boolean {
  return status === 'confirmed' || status === 'pending' || status === 'checked_in';
}

function CancellationSection({ booking }: { booking: MyBooking }) {
  const [requestedAt, setRequestedAt] = useState<string | null>(
    booking.cancellation_requested_at ?? null,
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (requestedAt) {
    return (
      <>
        <h2 className="font-serif text-lg font-semibold text-[var(--ollio-ink)]">
          Cancellation requested
        </h2>
        <p className="mt-1.5 text-sm text-muted">
          You asked us to cancel this booking on {formatRequestedAt(requestedAt)}. We&apos;ll be in
          touch by email to confirm the refund under the terms above.
        </p>
      </>
    );
  }

  return (
    <>
      <h2 className="font-serif text-lg font-semibold text-[var(--ollio-ink)]">Need to cancel?</h2>
      <p className="mt-1.5 text-sm text-muted">
        Send us a request and we&apos;ll reply by email to confirm the refund under the terms
        above.
      </p>
      <Button
        variant="secondary"
        size="md"
        className="mt-4"
        loading={sending}
        onClick={async () => {
          setSending(true);
          setError(null);
          const { data } = await requestBookingCancellation({ path: { id: booking.id } });
          setSending(false);
          if (!data) {
            // Nothing was recorded, so the button stays. Telling the guest it
            // worked would leave them waiting for a reply to a request nobody
            // received.
            setError("We couldn't send that request. Please try again, or write to us.");
            return;
          }
          setRequestedAt(data.cancellation_requested_at);
        }}
      >
        Request cancellation
      </Button>
      {error ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </>
  );
}

// formatRequestedAt renders the day the guest asked. Date only: the hour adds
// nothing to "we are dealing with it" and invites a precision the reply time
// will not match.
function formatRequestedAt(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}
